import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid2,
  Typography,
  Button,
  Chip,
  Paper,
} from '@mui/material';
import {
  ArrowRight,
  Building2,
  MessageSquare,
  PhoneCall,
  CheckCircle2,
  ChevronRight,
  TrendingUp,
  Cpu,
  Layers,
  Zap,
  Users,
  Calendar,
  BarChart3,
  Globe,
  ExternalLink,
  MapPinned,
} from 'lucide-react';
import InstagramIcon from '@mui/icons-material/Instagram';
import WovenLightHero from '../components/common/WovenLightHero';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import indiaMap from '@svg-maps/india';

const servedStates = [
  { id: 'mh', name: 'Maharashtra', city: 'Pune' },
  { id: 'hr', name: 'Haryana', city: 'Gurugram' },
  { id: 'wb', name: 'West Bengal', city: 'Kolkata' },
  { id: 'gj', name: 'Gujarat', city: 'Ahmedabad' },
  { id: 'ka', name: 'Karnataka', city: 'Bengaluru' },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [activeServiceState, setActiveServiceState] = useState(0);
  const [markerPositions, setMarkerPositions] = useState({});
  const activeServiceStateIndex = activeServiceState % servedStates.length;

  useEffect(() => {
    const rotation = window.setInterval(() => {
      setActiveServiceState((current) => (current + 1) % servedStates.length);
    }, 2600);
    return () => window.clearInterval(rotation);
  }, [servedStates.length]);

  useEffect(() => {
    const svg = document.getElementById('india-map-svg');
    if (!svg) return;

    const updatePositions = () => {
      const viewBox = svg.viewBox.baseVal;
      const nextPositions = {};

      svg.querySelectorAll('path[data-state-id]').forEach((pathNode) => {
        const stateId = pathNode.getAttribute('data-state-id');
        const isServed = servedStates.some((state) => state.id === stateId);
        if (!isServed || !stateId) return;

        const bounds = pathNode.getBBox();
        const centerX = ((bounds.x + bounds.width / 2) / viewBox.width) * 100;
        const centerY = ((bounds.y + bounds.height / 2) / viewBox.height) * 100;

        nextPositions[stateId] = { top: `${centerY}%`, left: `${centerX}%` };
      });

      setMarkerPositions(nextPositions);
    };

    updatePositions();
    window.addEventListener('resize', updatePositions);

    return () => window.removeEventListener('resize', updatePositions);
  }, [servedStates]);

  const handleTalkToUs = () => {
    window.open('https://wa.me/919112614174', '_blank', 'noopener,noreferrer');
  };

  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Acolead capabilities data
  const acoleadCapabilities = [
    {
      title: 'Lead Management',
      icon: <Users size={20} />,
      desc: 'Centralize inbound leads from digital marketing, portals, channel partners, and direct walk-ins into a unified dashboard.',
      features: ['Unified lead inbox', 'Multi-channel attribution', 'Status tracking pipeline', 'Custom tag classification'],
    },
    {
      title: 'Smart Lead Assignment',
      icon: <Cpu size={20} />,
      desc: 'Automated round-robin and rules-based lead distribution ensures instant lead routing to the right sales executive without delay.',
      features: ['Instant notification alerts', 'Rule-based routing logic', 'Workload balancing', 'Response time monitoring'],
    },
    {
      title: 'Follow-ups & Scheduling',
      icon: <Calendar size={20} />,
      desc: 'Never miss a prospective buyer. Automated reminders, site visit scheduling, and seamless call logging for sales teams.',
      features: ['Automated follow-up reminders', 'Site visit scheduling calendar', 'Call outcome logging', 'Overdue alert notifications'],
    },
    {
      title: 'Inventory Management',
      icon: <Building2 size={20} />,
      desc: 'Real-time property inventory tracking across units, block releases, pricing tiers, and booking status updates.',
      features: ['Unit availability matrix', 'Live pricing updates', 'Hold & booking lock state', 'Tower & floor plan mapping'],
    },
    {
      title: 'Team Management',
      icon: <Layers size={20} />,
      desc: 'Role-based access controls for sales agents, managers, telecallers, and channel partner managers with full activity audit trails.',
      features: ['Hierarchical permissions', 'Agent activity timeline', 'Performance benchmarks', 'Target vs achievement tracking'],
    },
    {
      title: 'Channel Partner Management',
      icon: <Globe size={20} />,
      desc: 'Empower broker networks and channel partners with dedicated lead submission portals and commission visibility.',
      features: ['CP portal access', 'Lead duplication check', 'Commission tracking', 'Exclusive payout reports'],
    },
    {
      title: 'WhatsApp Communication',
      icon: <WhatsAppIcon sx={{ fontSize: 24 }} />,
      desc: 'Direct WhatsApp API integration inside CRM. Send brochures, site visit details, and automated templates with 1-click.',
      features: ['1-Click WhatsApp messaging', 'Document & brochure sharing', 'Chat history in lead profile', 'Automated greeting triggers'],
    },
    {
      title: 'Reports & Insights',
      icon: <BarChart3 size={20} />,
      desc: 'Executive analytics dashboards covering lead conversion rates, campaign ROI, team velocity, and sales forecasting.',
      features: ['Real-time pipeline analytics', 'Campaign ROI reports', 'Sales velocity metrics', 'Exportable custom reports'],
    },
  ];

  const clientLogoSlots = [
    { name: 'Desai Estate', src: '/Client Logos/Desai Estate.jpeg' },
    { name: 'Direct Resale', src: '/Client Logos/Direct Resale.png' },
    { name: 'Global Realty', src: '/Client Logos/Globle consultancy.jpg' },
    { name: 'Inspirational Homes', src: '/Client Logos/Inspirational Homes.png' },
    { name: 'KD Asset Builder', src: '/Client Logos/Kd Asset Builder.png' },
    { name: 'Leadsinn', src: '/Client Logos/leadsinn.jpeg' },
    { name: 'Mittal Brothers', src: '/Client Logos/Mittal Brothers.png' },
    { name: 'PotterzWheel Realty', src: '/Client Logos/PotterzWheel Realty.jpeg' },
    { name: 'PropMentor', src: '/Client Logos/PROPMENTORLOGO.jpeg' },
    { name: 'Pune Property Guide', src: '/Client Logos/Pune_Property_Guide__1_ (1).png' },
    { name: 'Shri Gurukeshav Projects', src: '/Client Logos/Shri_Gurukeshav _Logo (2).jpg' },
    { name: 'Sukran Realty', src: '/Client Logos/Sukran Realty.png' },
    { name: 'Jain Housing', src: '/Client Logos/WhatsApp Image 2026-09-18 at 5.45.21 PM (1).jpeg' },
    { name: 'Maharshi Group', src: '/Client Logos/WhatsApp Image 2026-09-28 at 6.57.51 PM.jpeg' },
    { name: 'Whitecollar', src: '/Client Logos/whitecollar.png' },
  ].filter((logo) => /estate|realty|homes|resale|property|builder|brothers|home|consultancy|sukran|whitecollar|lead|projects|jain|maharshi/i.test(logo.name));

  const capabilities = [
    {
      number: '01',
      title: 'Business Process Automation',
      description: 'Building technology that reduces repetitive work and brings greater structure to everyday business processes.',
      color: '#2563EB',
    },
    {
      number: '02',
      title: 'AI & Intelligent Automation',
      description: 'Applying AI and automation to customer engagement, communication, workflows, and business operations.',
      color: '#0D9488',
    },
    {
      number: '03',
      title: 'CRM & Sales Technology',
      description: 'Creating connected systems that help businesses organize customer journeys, sales activities, and team workflows.',
      color: '#4F46E5',
    },
    {
      number: '04',
      title: 'Communication Technology',
      description: 'Enabling businesses to manage and automate customer interactions across modern communication channels.',
      color: '#059669',
    },
    {
      number: '05',
      title: 'Data & Business Visibility',
      description: 'Transforming operational data into meaningful dashboards and insights that support better decision-making.',
      color: '#D97706',
    },
  ];

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* ========================================================================= */}
      {/* 1. UPGRADED THREE.JS HERO SECTION (WovenLightHero) */}
      {/* ========================================================================= */}
      <WovenLightHero />

      {/* ========================================================================= */}
      {/* 2. TRUST / INTRODUCTION */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#F8FAFC' }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 840, mx: 'auto', textAlign: 'center', mb: 7 }}>
            <Chip
              label="CORE PHILOSOPHY"
              sx={{
                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                color: '#2563EB',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                mb: 2,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.75rem' },
                letterSpacing: '-0.03em',
                color: '#0F172A',
                mb: 3,
              }}
            >
              Technology Designed for Real Business Challenges
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1.05rem', md: '1.15rem' },
                color: '#475569',
                lineHeight: 1.7,
              }}
            >
              Modern businesses do not need fragmented tools or generic software. They require integrated technology systems where CRM, AI intelligence, automated messaging, and communication channels operate in complete harmony.
            </Typography>
          </Box>

          {/* 4 Connected Capability Pillars */}
          <Grid2 container spacing={3}>
            {[
              {
                title: 'Software',
                desc: 'Specialized enterprise platforms like Acolead CRM built around industry-specific operational workflows.',
                icon: <Building2 size={26} />,
                color: '#2563EB',
              },
              {
                title: 'Artificial Intelligence',
                desc: 'Intelligent customer conversation models for instant response, lead scoring, and automated engagement.',
                icon: <Cpu size={26} />,
                color: '#4F46E5',
              },
              {
                title: 'Automation',
                desc: 'Zero-latency lead routing, status updates, follow-up scheduling, and workflow triggers that save hours.',
                icon: <Zap size={26} />,
                color: '#0D9488',
              },
              {
                title: 'Business Communication',
                desc: 'Official WhatsApp API, social media response, and intelligent calling technologies under one roof.',
                icon: <WhatsAppIcon sx={{ fontSize: 34 }} />,
                color: '#10B981',
              },
            ].map((pillar, idx) => (
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }} key={idx}>
                <Paper
                  elevation={0}
                  className="card-interactive"
                  sx={{
                    p: { xs: 2.25, md: 4 },
                    height: '100%',
                    minHeight: { xs: 132, md: 'auto' },
                    borderRadius: { xs: 3, md: 4 },
                    backgroundColor: '#FFFFFF',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    display: 'flex',
                    flexDirection: { xs: 'row', md: 'column' },
                    alignItems: { xs: 'center', md: 'stretch' },
                    justifyContent: 'space-between',
                    gap: { xs: 2, md: 0 },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
                      borderColor: 'rgba(37, 99, 235, 0.3)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 60, md: 52 },
                      height: { xs: 60, md: 52 },
                      flexShrink: 0,
                      borderRadius: '50%',
                      backgroundColor: `${pillar.color}12`,
                      color: pillar.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: { xs: 0, md: 3 },
                    }}
                  >
                    {pillar.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.12rem', md: '1.25rem' }, lineHeight: 1.2, color: '#0F172A', mb: { xs: 0.7, md: 1.5 } }}>
                      {pillar.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748B',
                        lineHeight: 1.5,
                        fontSize: { xs: '0.9rem', md: '0.875rem' },
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: { xs: 3, md: 'unset' },
                        overflow: 'hidden',
                      }}
                    >
                      {pillar.desc}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: { xs: 'flex', md: 'none' },
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: { xs: 44, md: 48 },
                      height: { xs: 44, md: 48 },
                      flexShrink: 0,
                      borderRadius: '50%',
                      backgroundColor: `${pillar.color}12`,
                      color: pillar.color,
                    }}
                  >
                    <ArrowRight size={23} />
                  </Box>
                </Paper>
              </Grid2>
            ))}
          </Grid2>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 3. INDUSTRIES WE SERVE */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 11 }, pt: { xs: 5, md: 4 }, backgroundColor: '#FFFFFF', borderTop: '1px solid rgba(15, 23, 42, 0.06)', borderBottom: '1px solid rgba(15, 23, 42, 0.06)' }}>
        <Container
          maxWidth={false}
          sx={{
            px: { xs: 2, sm: 4, md: 7, lg: 10 },
          }}
        >
          <Grid2 container spacing={{ xs: 5, md: 8 }} alignItems="flex-start">
            <Grid2 size={{ xs: 12, md: 7 }}>
              <Chip
                label="INDUSTRIES WE SERVE"
                sx={{
                  backgroundColor: 'rgba(37, 99, 235, 0.08)',
                  color: '#2563EB',
                  fontWeight: 800,
                  fontSize: '0.75rem',
                  letterSpacing: '0.08em',
                  mb: 2,
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.6rem', md: '2.8rem' },
                  lineHeight: 1.15,
                  letterSpacing: '-0.03em',
                  color: '#0F172A',
                  mb: 2.5,
                  maxWidth: 680,
                }}
              >
                Real Estate Challenges. Technology-Led Solutions.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#475569',
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.75,
                  maxWidth: 700,
                }}
              >
                We understand that growing real estate businesses deal with fragmented information, multiple lead sources, repetitive communication, complex sales workflows, and limited visibility. We build technology that brings greater structure, automation, and intelligence to these processes.
              </Typography>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 5 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
              <Box sx={{ width: '100%', maxWidth: { xs: '100%', md: 560 }, ml: { xs: 0, md: 'auto' } }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: '#0F172A',
                    fontWeight: 800,
                    fontSize: { xs: '1.25rem', md: '1rem' },
                    lineHeight: 1.15,
                    letterSpacing: '-0.02em',
                    mb: 2.5,
                  }}
                >
                  
                </Typography>
                <Box
                  component="video"
                  src="/web site animation.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  aria-label="Coalitionify real estate technology animation"
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    maxHeight: { xs: 420, md: 300},
                    objectFit: 'contain',
                    borderRadius: 4,
                    border: '1px solid rgba(37, 99, 235, 0.12)',
                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                    backgroundColor: '#FFFFFF',
                  }}
                />
              </Box>
            </Grid2>
          </Grid2>

          <Box sx={{ mt: { xs: 7, md: 9 } }}>
            <Box sx={{ textAlign: 'center', mb: 3.5 }}>
              <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1 }}>
                Trusted by Real Estate Businesses
              </Typography>
              <Typography sx={{ color: '#94A3B8', fontSize: '0.9rem' }}>
                
              </Typography>
            </Box>

            <Box
              className="client-logo-marquee"
              sx={{
                width: '100%',
                overflow: 'hidden',
              }}
            >
              <Box className="client-logo-track">
                {[...clientLogoSlots, ...clientLogoSlots].map((slot, index) => (
                  <Box
                    key={`${slot.name}-${index}`}
                    className="client-logo-tile"
                    sx={{
                      width: { xs: 142, sm: 180, md: 200 },
                      height: { xs: 132, md: 154 },
                      flexShrink: 0,
                      px: { xs: 1.5, md: 2 },
                      py: { xs: 1.5, md: 2 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      borderRadius: 3,
                      backgroundColor: '#FFFFFF',
                      color: '#334155',
                      boxShadow: '0 5px 18px rgba(15, 23, 42, 0.04)',
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                      '&:hover': {
                        borderColor: 'rgba(37, 99, 235, 0.28)',
                        boxShadow: '0 12px 28px rgba(37, 99, 235, 0.1)',
                        transform: 'translateY(-3px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: ['Shri Gurukeshav Projects', 'Jain Housing', 'Maharshi Group'].includes(slot.name)
                          ? { xs: 96, md: 116 }
                          : { xs: 70, md: 92 },
                        width: '100%',
                        px: 1,
                        borderRadius: 2,
                        backgroundColor: '#F8FAFC',
                        border: '1px solid rgba(15, 23, 42, 0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        component="img"
                        src={slot.src}
                        alt={`${slot.name} logo`}
                        sx={{
                          maxWidth: ['Mittal Brothers', 'KD Asset Builder'].includes(slot.name) ? { xs: '98%', md: '100%' } : '100%',
                          maxHeight:
  slot.name === 'Mittal Brothers'
    ? { xs: 150, md: 180 }
    : slot.name === 'KD Asset Builder'
      ? { xs: 90, md: 110 }
      : ['Shri Gurukeshav Projects', 'Jain Housing', 'Maharshi Group'].includes(slot.name)
        ? { xs: 92, md: 112 }
      : { xs: 58, md: 76 },
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 4. SOLUTIONS SECTION */}
      {/* ========================================================================= */}
      <Box id="solutions-section" sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 7, textAlign: 'center' }}>
            <Chip
              label="SOLUTION ECOSYSTEM"
              sx={{
                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                color: '#2563EB',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                mb: 2,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.75rem' },
                letterSpacing: '-0.03em',
                color: '#0F172A',
                mb: 2,
              }}
            >
              One Technology Ecosystem. Multiple Possibilities.
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', maxWidth: 640, mx: 'auto' }}>
              Explore our core product offerings designed to simplify business operations and revolutionize customer engagement.
            </Typography>
          </Box>

          <Grid2 container spacing={4}>
            {/* 1. Flagship Product: Acolead CRM (Prominent Wide Card) */}
            <Grid2 size={12}>
              <Paper
                elevation={0}
                className="card-interactive"
                sx={{
                  p: { xs: 3, md: 6 },
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, #070D19 0%, #0F172A 100%)',
                  color: '#FFFFFF',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 60px rgba(15, 23, 42, 0.2)',
                    borderColor: 'rgba(96, 165, 250, 0.4)',
                  },
                }}
              >
                <Grid2 container spacing={{ xs: 2, md: 4 }} alignItems="center">
                  <Grid2 size={{ xs: 12, md: 7 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: { xs: 1.5, md: 2 }, flexWrap: 'nowrap', minWidth: 0 }}>
                      <Chip
                        label="FLAGSHIP PRODUCT"
                        sx={{
                          backgroundColor: 'rgba(37, 99, 235, 0.25)',
                          color: '#60A5FA',
                          fontWeight: 800,
                          fontSize: { xs: '0.68rem', md: '0.75rem' },
                          border: '1px solid rgba(96, 165, 250, 0.4)',
                          height: { xs: 30, md: 32 },
                          flexShrink: 0,
                          '& .MuiChip-label': {
                            px: { xs: 1.2, md: 1.5 },
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#94A3B8',
                          fontWeight: 600,
                          fontSize: { xs: '0.82rem', md: '0.75rem' },
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minWidth: 0,
                          maxWidth: '100%',
                        }}
                      >
                        Real Estate CRM Platform
                      </Typography>
                    </Box>

                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '2.1rem', md: '2.5rem' },
                        color: '#FFFFFF',
                        mb: { xs: 1.25, md: 2 },
                        letterSpacing: '-0.02em',
                        lineHeight: 1.05,
                      }}
                    >
                      Acolead CRM
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: '#94A3B8',
                        mb: { xs: 2.5, md: 4 },
                        fontSize: { xs: '0.98rem', md: '1.05rem' },
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: { xs: 5, md: 6 },
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}
                    >
                      The leading real-estate-focused CRM platform engineered specifically for builders, developers, agencies, mandate firms, and high-performance sales teams to manage leads, inventories, follow-ups, and channel partners seamlessly.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, width: '100%', maxWidth: 680, flexWrap: 'nowrap', alignItems: 'stretch' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleNavigate('/products')}
                        endIcon={<ArrowRight size={16} />}
                        sx={{
                          py: { xs: 1.2, md: 1.4 },
                          px: { xs: 1.2, md: 3 },
                          borderRadius: '14px',
                          fontWeight: 700,
                          fontSize: { xs: '0.76rem', md: '1rem' },
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          flex: '1 1 62%',
                          maxWidth: { xs: '55%', md: 'auto' },
                          background: 'linear-gradient(135deg, #1E90FF 0%, #2563EB 100%)',
                          boxShadow: 'none',
                          overflow: 'hidden',
                          '& .MuiButton-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                          },
                        }}
                      >
                        Explore Acolead
                      </Button>
                      <Button
                        variant="outlined"
                        component="a"
                        href="https://acolead.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<ExternalLink size={12} />}
                        sx={{
                          py: { xs: 1, md: 1.4 },
                          px: { xs: 8, md: 3 },
                          borderRadius: '14px',
                          fontWeight: 800,
                          fontSize: { xs: '0.64rem', md: '1rem' },
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          flex: '1 1 34%',
                          maxWidth: { xs: '46%', md: 'auto' },
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                          color: '#FFFFFF',
                          overflow: 'hidden',
                          '& .MuiButton-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                          },
                          '&:hover': {
                            borderColor: '#FFFFFF',
                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                          },
                        }}
                      >
                        Visit Acolead.com
                      </Button>
                    </Box>
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 5 }}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ color: '#60A5FA', mb: 2, fontWeight: 700 }}>
                        Key Capabilities Highlight:
                      </Typography>
                      {[
                        'Smart Round-Robin Lead Assignment',
                        'Real-Time Property Inventory Matrix',
                        'Channel Partner & Broker Network Portal',
                        'WhatsApp API Integration & 1-Click Messaging',
                        'Executive Pipeline & Conversion Analytics',
                      ].map((item, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                          <CheckCircle2 size={18} style={{ color: '#10B981', flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ color: '#E2E8F0', fontSize: '0.9rem' }}>
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Grid2>
                </Grid2>
              </Paper>
            </Grid2>

            {/* 2. AI-Powered Social Engagement */}
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                className="card-interactive"
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 4,
                  backgroundColor: '#F8FAFC',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
                    borderColor: 'rgba(37, 99, 235, 0.3)',
                  },
                }}
              >
                <Box>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      backgroundColor: 'rgba(225, 48, 108, 0.1)',
                      color: '#E1306C',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <InstagramIcon style={{ fontSize: 24 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700,  fontSize: { xs: '1rem', md: '1.3rem' }, color: '#0F172A', mb: 1.5 }}>
                    AI-Powered Social Engagement
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.65, mb: 3 }}>
                    Automate Instagram and Facebook customer interactions with AI-driven direct messaging, comment responses, and lead capture workflows.
                  </Typography>
                </Box>
                <Button
                  onClick={() => handleNavigate('/products')}
                  endIcon={<ChevronRight size={16} />}
                  sx={{ justifyContent: 'flex-start', px: { xs: 2, md: 3 }, color: '#2563EB', fontWeight: 700 }}
                >
                  Learn More
                </Button>
              </Paper>
            </Grid2>

            {/* 3. WhatsApp Business & AI */}
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                className="card-interactive"
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 4,
                  backgroundColor: '#F8FAFC',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
                    borderColor: 'rgba(37, 99, 235, 0.3)',
                  },
                }}
              >
                <Box>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      backgroundColor: 'rgba(37, 211, 102, 0.1)',
                      color: '#25D366',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: 34 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.3rem', color: '#0F172A', mb: 1.5 }}>
                    WhatsApp Business & AI
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.65, mb: 3 }}>
                    Deploy WhatsApp Business API with automated conversational AI, instant brochure dispatch, broadcast templates, and CRM synchronization.
                  </Typography>
                </Box>
                <Button
                  onClick={() => handleNavigate('/products')}
                  endIcon={<ChevronRight size={16} />}
                  sx={{ justifyContent: 'flex-start', px: { xs: 2, md: 3 }, color: '#2563EB', fontWeight: 700 }}
                >
                  Learn More
                </Button>
              </Paper>
            </Grid2>

            {/* 4. AI Calling Solutions */}
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                className="card-interactive"
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 4,
                  backgroundColor: '#F8FAFC',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
                    borderColor: 'rgba(37, 99, 235, 0.3)',
                  },
                }}
              >
                <Box>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      backgroundColor: 'rgba(79, 70, 229, 0.1)',
                      color: '#4F46E5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <PhoneCall size={24} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.3rem', color: '#0F172A', mb: 1.5 }}>
                    AI Calling Solutions
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.65, mb: 3 }}>
                    Intelligent voice technologies for automated lead qualification, appointment reminders, and scalable telecommunication workflows.
                  </Typography>
                </Box>
                <Button
                  onClick={() => handleNavigate('/products')}
                  endIcon={<ChevronRight size={16} />}
                  sx={{ justifyContent: 'flex-start', px: { xs: 2, md: 3 }, color: '#2563EB', fontWeight: 700 }}
                >
                  Learn More
                </Button>
              </Paper>
            </Grid2>
          </Grid2>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 4. CAPABILITIES */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 11 }, backgroundColor: '#F8FAFC', borderTop: '1px solid rgba(15, 23, 42, 0.06)', borderBottom: '1px solid rgba(15, 23, 42, 0.06)' }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 760, mx: 'auto', textAlign: 'center', mb: 6 }}>
            <Chip
              label="CAPABILITIES"
              sx={{
                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                color: '#2563EB',
                fontWeight: 800,
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                mb: 2,
              }}
            />
            <Typography variant="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.5rem', md: '2.75rem' }, color: '#0F172A', letterSpacing: '-0.03em', mb: 2 }}>
              What We Know How to Build
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', lineHeight: 1.7, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Our capabilities go beyond individual products, helping businesses turn complex operations into connected, measurable, and easier-to-manage systems.
            </Typography>
          </Box>

          <Grid2 container spacing={3}>
            {capabilities.map((capability) => (
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }} key={capability.number}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    minHeight: 250,
                    borderRadius: 3,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.25s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      borderColor: `${capability.color}55`,
                      boxShadow: `0 16px 32px -12px ${capability.color}35`,
                    },
                  }}
                >
                  <Typography sx={{ color: capability.color, fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.08em', mb: 3 }}>
                    {capability.number}
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#0F172A', fontWeight: 800, fontSize: '1.15rem', lineHeight: 1.3, mb: 1.5 }}>
                    {capability.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.65 }}>
                    {capability.description}
                  </Typography>
                </Paper>
              </Grid2>
            ))}
          </Grid2>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 5. FEATURED PRODUCT — ACOLEAD SHOWCASE */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#F8FAFC', borderTop: '1px solid rgba(15, 23, 42, 0.06)' }}>
        <Container maxWidth="lg">
          <Grid2 container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Chip
                label="MEET ACOLEAD"
                sx={{
                  backgroundColor: 'rgba(37, 99, 235, 0.08)',
                  color: '#2563EB',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.08em',
                  mb: 2,
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.5rem', md: '2.75rem' },
                  letterSpacing: '-0.03em',
                  color: '#0F172A',
                  mb: 2.5,
                }}
              >
                Real Estate CRM Built for the Way Your Business Works
              </Typography>
              <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.7, mb: 3 }}>
                Acolead is specifically designed to eliminate lost leads, inefficient follow-ups, and fragmented team communication. It provides real estate developers and agencies with complete control over their sales velocity and inventory pipeline.
              </Typography>

              {/* Bullet Points */}
              <Box sx={{ mb: 4 }}>
                {[
                  'Lead Management & Multi-Source Capture',
                  'Smart Lead Assignment & SLA Enforcement',
                  'Follow-up Reminders & Site Visit Scheduling',
                  'Unit Inventory & Real-Time Booking State',
                  'Sales Team & Telecaller Hierarchy',
                  'Channel Partner & Broker Network Management',
                ].map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <CheckCircle2 size={18} style={{ color: '#2563EB' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontSize: '0.95rem' }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Button
                variant="contained"
                color="primary"
                component="a"
                href="https://acolead.com"
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<ExternalLink size={18} />}
                sx={{ py: 1.4, px: 3.5, borderRadius: '10px', fontWeight: 600 }}
              >
                Visit Acolead.com
              </Button>
            </Grid2>

            {/* Dashboard Abstract Preview Graphic */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 3 },
                  borderRadius: { xs: 3, sm: 4 },
                  backgroundColor: '#070D19',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 25px 60px -15px rgba(15, 23, 42, 0.3)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 30px 80px -15px rgba(15, 23, 42, 0.4)',
                    borderColor: 'rgba(96, 165, 250, 0.3)',
                  },
                }}
              >
                {/* Mock CRM Header Bar */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 }, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.9, sm: 1.5 }, minWidth: 0 }}>
                    <Box sx={{ width: { xs: 9, sm: 12 }, height: { xs: 9, sm: 12 }, flexShrink: 0, borderRadius: '50%', backgroundColor: '#EF4444' }} />
                    <Box sx={{ width: { xs: 9, sm: 12 }, height: { xs: 9, sm: 12 }, flexShrink: 0, borderRadius: '50%', backgroundColor: '#F59E0B' }} />
                    <Box sx={{ width: { xs: 9, sm: 12 }, height: { xs: 9, sm: 12 }, flexShrink: 0, borderRadius: '50%', backgroundColor: '#10B981' }} />
                    <Typography variant="caption" sx={{ color: '#94A3B8', ml: { xs: 0.5, sm: 1 }, fontWeight: 700, fontSize: { xs: '0.68rem', sm: '0.75rem' } }}>
                      ACOLEAD CRM — Real Estate Dashboard
                    </Typography>
                  </Box>
                  <Chip label="LIVE SYSTEM" size="small" sx={{ flexShrink: 0, backgroundColor: 'rgba(16, 185, 129, 0.15)', color: '#10B981', fontWeight: 700, fontSize: { xs: '0.55rem', sm: '0.65rem' }, '& .MuiChip-label': { px: { xs: 0.8, sm: 1 } } }} />
                </Box>

                {/* Mock Metrics Row */}
                <Grid2 container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
                  {[
                    { label: 'Total Enquiries', val: '1,482', change: '+18%', color: '#60A5FA' },
                    { label: 'Active Site Visits', val: '249', change: '+24%', color: '#10B981' },
                    { label: 'Booked Units', val: '86', change: '+12%', color: '#A78BFA' },
                  ].map((m, i) => (
                    <Grid2 size={4} key={i}>
                      <Box sx={{ p: { xs: 1.25, sm: 2 }, height: { xs: 170, sm: 'auto' }, boxSizing: 'border-box', borderRadius: 2, backgroundColor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: { xs: '0.6rem', sm: '0.75rem' }, lineHeight: 1.45, display: 'block' }}>
                          {m.label}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#FFFFFF', mt: 0.5, fontSize: { xs: '1.35rem', sm: '1.5rem' } }}>
                          {m.val}
                        </Typography>
                        <Typography variant="caption" sx={{ color: m.color, fontWeight: 700, fontSize: { xs: '0.63rem', sm: '0.7rem' }, lineHeight: 1.45 }}>
                          {m.change} this month
                        </Typography>
                      </Box>
                    </Grid2>
                  ))}
                </Grid2>

                {/* Mock Lead Activity Table Snippet */}
                <Box sx={{ borderRadius: 2, backgroundColor: 'rgba(255, 255, 255, 0.03)', p: { xs: 1.25, sm: 2 }, border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                  <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, display: 'block', mb: 1.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    Recent High-Intent Leads
                  </Typography>
                  {[
                    { name: 'Rajesh Kumar', prop: '3 BHK Skyline Luxury', status: 'Site Visit Confirmed', agent: 'Priya S.', time: '2 mins ago' },
                    { name: 'Ananya Verma', prop: 'Penthouse Villa', status: 'Quote Sent', agent: 'Rahul M.', time: '14 mins ago' },
                    { name: 'Vikram Mehta', prop: 'Commercial Retail', status: 'Broker Assigned', agent: 'Amit K.', time: '45 mins ago' },
                  ].map((lead, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: { xs: 0.8, sm: 1 }, borderBottom: idx < 2 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none' }}>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: { xs: '0.78rem', sm: '0.85rem' } }}>
                          {lead.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: { xs: '0.68rem', sm: '0.75rem' } }}>
                          {lead.prop}
                        </Typography>
                      </Box>
                      <Chip label={lead.status} size="small" sx={{ flexShrink: 0, backgroundColor: 'rgba(37, 99, 235, 0.15)', color: '#60A5FA', fontSize: { xs: '0.58rem', sm: '0.7rem' }, '& .MuiChip-label': { px: { xs: 0.8, sm: 1 } } }} />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid2>
          </Grid2>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 5. ACOLEAD CAPABILITIES TABS */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              label="ACOLEAD CAPABILITIES"
              sx={{
                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                color: '#2563EB',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                mb: 2,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.1rem', md: '2.5rem' },
                color: '#0F172A',
                mb: 2,
              }}
            >
              Engineered for Real Estate Excellence
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', maxWidth: 640, mx: 'auto' }}>
              Explore the dedicated feature suite that makes Acolead the premier choice for modern real estate operations.
            </Typography>
          </Box>

          {/* Capabilities Grid */}
          <Grid2 container spacing={3}>
            {acoleadCapabilities.map((cap, index) => (
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Paper
                  elevation={0}
                  className="card-interactive"
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 3,
                    backgroundColor: '#F8FAFC',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
                      borderColor: 'rgba(37, 99, 235, 0.3)',
                    },
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        backgroundColor: 'rgba(37, 99, 235, 0.1)',
                        color: '#2563EB',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      {cap.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0F172A', mb: 1 }}>
                      {cap.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.875rem', lineHeight: 1.6, mb: 2 }}>
                      {cap.desc}
                    </Typography>
                  </Box>

                  <Box sx={{ pt: 1, borderTop: '1px dashed rgba(15, 23, 42, 0.08)' }}>
                    {cap.features.map((f, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: '#334155', fontWeight: 600, fontSize: '0.78rem', mb: 0.5 }}>
                        • {f}
                      </Typography>
                    ))}
                  </Box>
                </Paper>
              </Grid2>
            ))}
          </Grid2>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 6. HOW WE CREATE VALUE */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#070D19', color: '#FFFFFF' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="METHODOLOGY"
              sx={{
                backgroundColor: 'rgba(59, 130, 246, 0.15)',
                color: '#60A5FA',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                mb: 2,
                border: '1px solid rgba(96, 165, 250, 0.3)',
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.25rem', md: '2.75rem' },
                color: '#FFFFFF',
                mb: 2,
              }}
            >
              From Business Challenge to Working Solution
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: 640, mx: 'auto' }}>
              Our approach starts with understanding the business problem—not simply implementing technology.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch', gap: { xs: 1.5, md: 2 }, overflow: 'visible', mx: { xs: 0, md: 0 }, px: { xs: 0, md: 0 } }}>
            {[
              {
                num: '01',
                step: 'Understand',
                desc: 'We study the business, users, workflows, challenges, and desired outcomes.',
              },
              {
                num: '02',
                step: 'Define',
                desc: 'We identify opportunities where technology, automation, and better processes can create meaningful value.',
              },
              {
                num: '03',
                step: 'Design',
                desc: 'We design practical workflows and user experiences around real business requirements.',
              },
              {
                num: '04',
                step: 'Build & Integrate',
                desc: 'We develop, configure, and connect the technology required to support the solution.',
              },
              {
                num: '05',
                step: 'Improve',
                desc: 'We learn from real-world usage and continue improving the product and workflows over time.',
              },
            ].map((m, idx, steps) => (
              <React.Fragment key={m.num}>
                <Paper
                  elevation={0}
                  sx={{
                   p: { xs: 1.25, md: 2.75 },
                    flex: { xs: '0 0 auto', md: '1 1 0' },
                    width: { xs: '90%', md: 'auto' },
                    alignSelf: { xs: 'center', md: 'auto' },
                   minHeight: { xs: 135, md: 280 },
                   height: { xs: 135, md: 280 },
                   borderRadius: { xs: 3.5, md: 2.5 },
                    background: { xs: '#FFFFFF', md: 'linear-gradient(155deg, rgba(18, 28, 48, 0.94), rgba(11, 18, 32, 0.94))' },
                    border: { xs: '1px solid rgba(15, 23, 42, 0.08)', md: '1px solid rgba(255, 255, 255, 0.1)' },
                    borderTop: { xs: '1px solid rgba(15, 23, 42, 0.08)', md: '3px solid #3B82F6' },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: { xs: 'flex-start', md: 'space-between' },
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: { xs: 'none', md: 'translateY(-4px)' },
                      borderColor: 'rgba(96, 165, 250, 0.4)',
                      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
                    },
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 0.25, md: 2 }}}>
                      <Box sx={{ width: { xs: 28, md: 38 }, height: { xs: 28, md: 38 }, borderRadius: 1.5, display: 'grid', placeItems: 'center', backgroundColor: { xs: 'transparent', md: 'rgba(59, 130, 246, 0.16)' }, color: { xs: '#2563EB', md: '#60A5FA' }, fontSize: '0.9rem', fontWeight: 800 }}>
                        {m.num}
                      </Box>
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 750,
                        color: { xs: '#0F172A', md: '#FFFFFF' },
                       fontSize: { xs: '1.35rem', md: '1.16rem' },
                       mb: 0.5,
                        lineHeight: 1.08,
                      }}
                    >
                      {m.step}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: { xs: '#64748B', md: '#9FB0C8' },
                        fontSize: { xs: '0.85rem', md: '0.86rem' },
                        lineHeight: 1.3, 
                        display: '-webkit-box',
                        WebkitLineClamp: { xs: 2, md: 4 },
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: { xs: '100%', md: 'auto' },
                      }}
                    >
                      {m.desc}
                    </Typography>
                  </Box>
                </Paper>
                {idx < steps.length - 1 && (
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center', color: '#60A5FA', flex: '0 0 auto', height: { xs: 22, md: 'auto' }, transform: { xs: 'rotate(90deg)', md: 'none' } }}>
                    <ArrowRight size={22} />
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 7. WHERE WE SERVE — Premium Light Map Experience */}
      {/* ========================================================================= */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: '#F8FAFC',
          borderTop: '1px solid rgba(15, 23, 42, 0.06)',
          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background ambient glow effects */}
        <Box sx={{ position: 'absolute', top: '10%', left: '-5%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37, 99, 235, 0.05) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: '5%', right: '-5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(16, 185, 129, 0.05) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />

        {/* Subtle grid overlay */}
        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.4, backgroundImage: 'linear-gradient(rgba(37, 99, 235, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(37, 99, 235, 0.03) 1px, transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Section Header — Centered */}
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Chip
              label="WHERE WE SERVE"
              sx={{
                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                color: '#2563EB',
                fontWeight: 800,
                fontSize: '0.72rem',
                letterSpacing: '0.12em',
                mb: 2.5,
                border: '1px solid rgba(37, 99, 235, 0.18)',
              }}
            />
            <Typography variant="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.25rem', md: '3rem' }, lineHeight: 1.12, color: '#0F172A', letterSpacing: '-0.03em', mb: 2 }}>
              CRM Technology Powering{' '}
              <Box component="span" sx={{ background: 'linear-gradient(135deg, #2563EB, #059669)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Real Estate Across India
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ color: '#475569', fontSize: { xs: '1rem', md: '1.12rem' }, lineHeight: 1.75, maxWidth: 680, mx: 'auto' }}>
              Our CRM platform serves real estate teams across India's key markets — from lead capture to site visits, bookings, and beyond.
            </Typography>
          </Box>

          {/* Main Content: Centered India Map */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Paper
              elevation={0}
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: { xs: '100%', md: 760 },
                height: { xs: 420, md: 520 },
                borderRadius: 4,
                overflow: 'hidden',
                background: 'linear-gradient(155deg, #FFFFFF 0%, #F8FAFC 100%)',
                border: '1px solid rgba(37, 99, 235, 0.15)',
                boxShadow: '0 20px 50px rgba(15, 23, 42, 0.06), 0 2px 8px rgba(37, 99, 235, 0.04)',
                mx: 'auto',
              }}
            >
              {/* Grid pattern inside map */}
              <Box className="india-map-grid" sx={{ position: 'absolute', inset: 0, opacity: 0.5 }} />

              {/* Radial glow behind map */}
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '70%', height: '70%', borderRadius: '50%', background: 'radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />

              {/* Top badge */}
              <Box sx={{ position: 'absolute', right: 20, top: 18, px: 1.8, py: 0.8, borderRadius: 2, background: '#FFFFFF', border: '1px solid rgba(37, 99, 235, 0.15)', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.05)', zIndex: 2 }}>
                <Typography sx={{ color: '#2563EB', fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>5 States </Typography>
              </Box>

              {/* SVG Map */}
              <Box sx={{ position: 'absolute', inset: { xs: '3% 10%', md: '2% 18%' }, zIndex: 1 }}>
                <svg id="india-map-svg" viewBox={indiaMap.viewBox} role="img" aria-label={indiaMap.label} width="100%" height="100%">
                  <defs>
                    <linearGradient id="served-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#0D9488" stopOpacity="0.2" />
                    </linearGradient>
                    <linearGradient id="active-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#2563EB" stopOpacity="0.85" />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.95" />
                    </linearGradient>
                    <filter id="glow-filter">
                      <feGaussianBlur stdDeviation="2.5" result="blur" />
                      <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>
                  {indiaMap.locations.map((location) => {
                    const isServed = servedStates.some((state) => state.id === location.id);
                    const isActive = servedStates[activeServiceStateIndex].id === location.id;
                    return (
                      <path
                        key={location.id}
                        data-state-id={location.id}
                        d={location.path}
                        aria-label={location.name}
                        fill={isActive ? 'url(#active-gradient)' : isServed ? 'url(#served-gradient)' : '#E2E8F0'}
                        fillOpacity={isActive ? 1 : isServed ? 1 : 0.6}
                        stroke={isActive ? '#1D4ED8' : isServed ? 'rgba(37, 99, 235, 0.4)' : '#CBD5E1'}
                        strokeWidth={isActive ? '2' : isServed ? '1' : '0.8'}
                        filter={isActive ? 'url(#glow-filter)' : 'none'}
                        style={{ transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)', cursor: isServed ? 'pointer' : 'default' }}
                        className={isActive ? 'india-state-active' : ''}
                      />
                    );
                  })}
                </svg>
              </Box>

              {/* Animated Markers */}
              <Box sx={{ position: 'absolute', inset: { xs: '3% 10%', md: '2% 18%' }, zIndex: 3, pointerEvents: 'none' }}>
                {servedStates.map((state, index) => (
                  <Box
                    key={`${state.id}-${state.city}`}
                    sx={{
                      position: 'absolute',
                      top: markerPositions[state.id]?.top || '50%',
                      left: markerPositions[state.id]?.left || '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'auto',
                    }}
                  >
                    <Box
                      className={index === activeServiceStateIndex ? 'crm-marker-pulse' : ''}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: index === activeServiceStateIndex ? 36 : 0,
                        height: index === activeServiceStateIndex ? 36 : 0,
                        borderRadius: '50%',
                        border: '2px solid rgba(37, 99, 235, 0.4)',
                        opacity: index === activeServiceStateIndex ? 1 : 0,
                        transition: 'all 0.4s ease',
                      }}
                    />
                    <Box
                      sx={{
                        width: index === activeServiceStateIndex ? 14 : 9,
                        height: index === activeServiceStateIndex ? 14 : 9,
                        borderRadius: '50%',
                        background: index === activeServiceStateIndex ? 'linear-gradient(135deg, #1D4ED8, #2563EB)' : '#2563EB',
                        border: index === activeServiceStateIndex ? '2.5px solid #FFFFFF' : '2px solid #FFFFFF',
                        boxShadow: index === activeServiceStateIndex ? '0 0 16px rgba(37, 99, 235, 0.6), 0 4px 10px rgba(0,0,0,0.15)' : '0 2px 6px rgba(37, 99, 235, 0.3)',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                      }}
                      onClick={() => setActiveServiceState(index)}
                    />
                    {index === activeServiceStateIndex && (
                      <Box
                        className="service-map-popup"
                        sx={{
                          position: 'absolute',
                          top: 22,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          minWidth: { xs: 145, md: 170 },
                          p: { xs: 1.5, md: 1.8 },
                          borderRadius: 2.5,
                          background: '#FFFFFF',
                          border: '1px solid rgba(37, 99, 235, 0.2)',
                          color: '#0F172A',
                          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.14), 0 2px 6px rgba(37, 99, 235, 0.08)',
                          textAlign: 'left',
                          zIndex: 10,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981', boxShadow: '0 0 8px rgba(16, 185, 129, 0.5)' }} />
                          <Typography sx={{ color: '#059669', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            CRM Active
                          </Typography>
                        </Box>
                        <Typography sx={{ color: '#0F172A', fontSize: { xs: '0.82rem', md: '0.88rem' }, fontWeight: 800, lineHeight: 1.3 }}>{state.name}</Typography>
                        <Typography sx={{ color: '#2563EB', fontSize: { xs: '0.68rem', md: '0.72rem' }, fontWeight: 600, mt: 0.3 }}>{state.city}</Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>

              <Box sx={{ position: 'absolute', left: 20, bottom: 18, display: 'flex', flexDirection: 'column', gap: 0.8, zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(135deg, #1D4ED8, #2563EB)', boxShadow: '0 2px 6px rgba(37, 99, 235, 0.3)' }} />
                  <Typography sx={{ color: '#475569', fontSize: '0.66rem', fontWeight: 600 }}>Active CRM region</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(37, 99, 235, 0.3)' }} />
                  <Typography sx={{ color: '#64748B', fontSize: '0.66rem', fontWeight: 600 }}>Served market</Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 8. WHY COALITIONIFY */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#F8FAFC' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              label="WHY COALITIONIFY"
              sx={{
                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                color: '#2563EB',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                mb: 2,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#0F172A',
              }}
            >
              Why Coalitionify?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#64748B',
                maxWidth: 680,
                mx: 'auto',
                mt: 1.5,
                lineHeight: 1.6,
              }}
            >
              We combine technology, domain expertise, and a customer-first approach to help businesses build a smarter and more connected future.
            </Typography>
          </Box>

          <Grid2 container spacing={4}>
            {[
              {
                title: 'Business-Focused Technology',
                desc: 'We build solutions around real operational and customer engagement challenges.',
                icon: <Building2 size={24} />,
              },
              {
                title: 'Innovation with Purpose',
                desc: 'We explore AI and emerging technologies with a focus on practical business applications.',
                icon: <Cpu size={24} />,
              },
              {
                title: 'Connected Solutions',
                desc: 'We bring together software, communication and automation to support more efficient workflows.',
                icon: <Layers size={24} />,
              },
              {
                title: 'Built to Evolve',
                desc: 'Technology requirements change. Our approach is focused on building solutions that can grow with changing business needs.',
                icon: <TrendingUp size={24} />,
              },
            ].map((item, idx) => (
              <Grid2 size={{ xs: 12, sm: 6 }} key={idx}>
                <Paper
                  elevation={0}
                  className="card-interactive"
                  sx={{
                    p: { xs: 2.25, md: 4 },
                    minHeight: { xs: 150, md: 154 },
                    height: { xs: 'auto', sm: '100%' },
                    borderRadius: 4,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1.5, md: 3 },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: { xs: 'none', md: 'translateY(-4px)' },
                      boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
                      borderColor: 'rgba(37, 99, 235, 0.3)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 56, md: 80 },
                      height: { xs: 56, md: 80 },
                      borderRadius: 3,
                      backgroundColor: 'rgba(37, 99, 235, 0.1)',
                      color: '#2563EB',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: '#0F172A',
                        mb: 0.75,
                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                        lineHeight: 1.2,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748B',
                        lineHeight: 1.45,
                        fontSize: { xs: '0.82rem', md: '0.875rem' },
                        display: '-webkit-box',
                        WebkitLineClamp: { xs: 4, md: 3 },
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: { xs: 34, md: 46 },
                      height: { xs: 34, md: 46 },
                      borderRadius: '50%',
                      backgroundColor: 'rgba(37, 99, 235, 0.08)',
                      color: '#2563EB',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <ArrowRight size={18} />
                  </Box>
                </Paper>
              </Grid2>
            ))}
          </Grid2>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 8. HOME FINAL CTA */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              py: { xs: 5, md: 8 },
              px: { xs: 3, md: 5 },
              borderRadius: 5,
              background: 'linear-gradient(135deg, #070D19 0%, #1E293B 100%)',
              color: '#FFFFFF',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 30px 80px -20px rgba(15, 23, 42, 0.4)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'scale(1.01)',
                boxShadow: '0 40px 100px -20px rgba(15, 23, 42, 0.5)',
              },
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', position: 'relative', zIndex: 1 }}>
              <Chip
                label="GET STARTED TODAY"
                sx={{
                  backgroundColor: 'rgba(59, 130, 246, 0.2)',
                  color: '#60A5FA',
                  fontWeight: 800,
                  fontSize: { xs: '0.68rem', md: '0.75rem' },
                  letterSpacing: '0.08em',
                  mb: 2.5,
                  border: '1px solid rgba(96, 165, 250, 0.3)',
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.9rem', md: '3rem' },
                  color: '#FFFFFF',
                  mb: 2.5,
                  letterSpacing: '-0.03em',
                  lineHeight: 0.95,
                }}
              >
                Let's Build Something Smarter
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#94A3B8',
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  lineHeight: 1.5,
                  mb: 4,
                }}
              >
                Looking for technology that can help simplify your business operations and customer communication? Explore our products and solutions or connect with our team.
              </Typography>

              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', alignItems: 'stretch', flexWrap: 'nowrap', width: '100%', maxWidth: 760, mx: 'auto' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => handleNavigate('/products')}
                  endIcon={<ArrowRight size={18} />}
                  sx={{
                    py: { xs: 1.5, md: 1.5 },
                    px: { xs: 1.4, md: 4 },
                    borderRadius: '18px',
                    fontWeight: 700,
                    fontSize: { xs: '0.8rem', md: '1rem' },
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    flex: '1 1 62%',
                    maxWidth: { xs: '64%', md: 'auto' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    background: 'linear-gradient(135deg, #1E90FF 0%, #2563EB 100%)',
                    boxShadow: 'none',
                    color: '#FFFFFF',
                    '& .MuiButton-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1E90FF 0%, #2563EB 100%)',
                    },
                  }}
                >
                  Explore Our Solutions
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleTalkToUs}
                  sx={{
                    py: { xs: 1.5, md: 1.5 },
                    px: { xs: 1.4, md: 4 },
                    borderRadius: '18px',
                    fontWeight: 700,
                    fontSize: { xs: '0.8rem', md: '1rem' },
                    lineHeight: 1.2,
                    borderColor: 'rgba(255, 255, 255, 0.45)',
                    color: '#FFFFFF',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    flex: '1 1 38%',
                    maxWidth: { xs: '36%', md: 'auto' },
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '& .MuiButton-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                    '&:hover': {
                      borderColor: '#FFFFFF',
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    },
                  }}
                >
                  Talk to Us
                </Button>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
